import { useRef, useState, type FormEvent } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Eye, EyeOff, Lock, ShieldCheck, User } from "lucide-react";
import { Button, ErrorText } from "../components/ui";
import { LoginEntrance } from "../components/entrance/LoginEntrance";
import { useAuth } from "../context/AuthContext";
import { useBranding } from "../context/BrandingContext";
import { ApiError } from "../lib/api";

/**
 * Sign-in, staged by the cinematic entrance (see LoginEntrance): the graphite
 * pressing-room, the steam, the mark reveal, then this card.
 *
 * The card is the concept's third frame — a dark centred panel — built from
 * the system's own parts: the gold action is the Button's `secondary` variant
 * (the one accent-background treatment in the system, with its measured
 * dark-on-gold text), spacing and radii are the standard scale, and errors use
 * the standard boxed alert.
 *
 * The fields are composed locally rather than through the shared Input,
 * because that component is a floating-label control for the light theme and
 * the concept card uses static labels on graphite. Restyling the shared Input
 * for one screen would push a dark variant into every form in the product;
 * a local composition keeps the entrance's look scoped to the entrance.
 *
 * Renders nothing until branding resolves, so there is never a flash of
 * default branding before the owner's own appears.
 */
export function LoginPage() {
  const { user, login, loading } = useAuth();
  const { branding, language, loading: brandingLoading } = useBranding();
  const navigate = useNavigate();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const usernameRef = useRef<HTMLInputElement | null>(null);

  const ar = language === "ar";

  if (brandingLoading) return null;
  if (branding.setupRequired) return <Navigate to="/setup" replace />;
  if (!loading && user) return <Navigate to="/dashboard" replace />;

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(username, password);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      // The server returns one generic code for every credential failure, so
      // the message here is generic too. Saying "no such user" would leak
      // exactly what the API is careful not to.
      if (err instanceof ApiError && err.code === "account_locked") {
        setError(
          ar
            ? "تم قفل الحساب مؤقتاً بعد محاولات فاشلة متكررة."
            : "This account is temporarily locked after repeated failed attempts.",
        );
      } else if (err instanceof ApiError && err.code === "rate_limited") {
        setError(ar ? "محاولات كثيرة. حاول بعد قليل." : "Too many attempts. Please wait and try again.");
      } else {
        setError(ar ? "اسم المستخدم أو كلمة المرور غير صحيحة." : "Incorrect username or password.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Focus once the card has landed — but only for precise pointers (on touch,
  // a programmatic focus throws the keyboard over the final frame of the
  // entrance), and only if the person has not already chosen a field: the
  // click that skips the cinema can itself land on the password input, and
  // yanking focus back to username 640ms later would discard their choice.
  const focusWhenSettled = () => {
    const active = document.activeElement;
    if (active && active !== document.body) return;
    if (window.matchMedia("(pointer: fine)").matches) {
      usernameRef.current?.focus();
    }
  };

  // Placeholders are ink-400, not ink-500: on this field fill ink-500 measures
  // 3.41:1, under the 4.5:1 AA minimum for 13px text. Having a visible label
  // above the field is not a WCAG exemption — the exemptions are for inactive
  // components, decoration and logotypes, and a hint inside a live input is
  // none of those. ink-400 measures 6.5:1 and still sits clearly below the
  // ink-100 value text, so the hierarchy survives.
  const fieldClass = `h-12 w-full rounded-lg border border-white/[0.13] bg-white/[0.05] ps-10 text-sm
    text-ink-100 outline-none transition-colors placeholder:text-ink-400
    focus:border-accent-500 focus:bg-white/[0.07]`;

  return (
    <LoginEntrance onSettled={focusWhenSettled}>
      <div className="rounded-2xl border border-white/[0.08] bg-[#171310]/[0.93] p-6 shadow-lg backdrop-blur-sm sm:p-7">
        <h1 className="text-xl font-semibold text-ink-50">
          {ar ? "أهلاً بعودتك" : "Welcome back"}
        </h1>
        <p className="mt-1 text-sm text-ink-400">
          {ar ? "سجّل الدخول للمتابعة إلى حسابك." : "Sign in to continue to your account."}
        </p>

        <form onSubmit={handleSubmit} className="mt-5 space-y-4">
          <div>
            <label htmlFor="login-username" className="mb-1.5 block text-xs font-medium text-ink-300">
              {ar ? "اسم المستخدم" : "Username"}
            </label>
            <div className="relative">
              <User className="pointer-events-none absolute start-3 top-1/2 size-4 -translate-y-1/2 text-ink-500" aria-hidden="true" />
              <input
                id="login-username"
                ref={usernameRef}
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                autoComplete="username"
                placeholder={ar ? "أدخل اسم المستخدم" : "Enter your username"}
                className={fieldClass}
              />
            </div>
          </div>

          <div>
            <label htmlFor="login-password" className="mb-1.5 block text-xs font-medium text-ink-300">
              {ar ? "كلمة المرور" : "Password"}
            </label>
            <div className="relative">
              <Lock className="pointer-events-none absolute start-3 top-1/2 size-4 -translate-y-1/2 text-ink-500" aria-hidden="true" />
              <input
                id="login-password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                placeholder={ar ? "أدخل كلمة المرور" : "Enter your password"}
                className={`${fieldClass} pe-10`}
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={
                  showPassword
                    ? ar
                      ? "إخفاء كلمة المرور"
                      : "Hide password"
                    : ar
                      ? "إظهار كلمة المرور"
                      : "Show password"
                }
                /* p-2 grows the touch target to 32px; end-1 keeps the icon in
                   the same optical position a bare end-3 button would have. */
                className="absolute end-1 top-1/2 -translate-y-1/2 rounded-md p-2 text-ink-500 transition-colors hover:text-ink-300"
              >
                {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
              </button>
            </div>
          </div>

          <ErrorText>{error}</ErrorText>

          {/* The one gold action on the screen — the concept's button, which is
              the system's `secondary` variant with its measured dark-on-gold
              foreground. */}
          <Button type="submit" variant="secondary" size="lg" className="w-full" loading={submitting}>
            {ar ? "تسجيل الدخول" : "Sign in"}
          </Button>
        </form>

        {/* ink-400 on this card measures 7.2:1; ink-500 would be 3.86:1 and
            fail AA for 12px text. The placeholders stay ink-500 because each
            field has a real visible label carrying the information. */}
        <p className="mt-5 flex items-center justify-center gap-1.5 text-xs text-ink-400">
          <ShieldCheck className="size-3.5 text-accent-500" aria-hidden="true" />
          {ar ? "وصول آمن إلى إدارة مغسلتك" : "Secure access to your laundry business"}
        </p>
      </div>
    </LoginEntrance>
  );
}
